import axios from "axios";
import { api } from "@/lib/api";
import type { CurrentUser } from "@/context/AuthContext";
import type { ZapisSati } from "@/lib/sati";

export interface CrewMember {
  id?: string;
  radnik_id: string;
  je_brigadir: boolean;
  vrijedi_od: string;
  vrijedi_do: string | null;
}
export interface Crew {
  id: string;
  gradiliste_id: string;
  naziv: string;
  aktivna: boolean;
  clanovi: CrewMember[];
}
export interface TaskInput {
  planirani_sati?: string | null;
  brigada_id: string;
  naziv: string;
  opis: string;
  planirani_od: string;
  planirani_do: string;
  status: "aktivan" | "zavrsen" | "otkazan";
}
export interface SiteTask extends TaskInput {
  id: string;
  gradiliste_id: string;
}
export type OutcomeStatus =
  "ostvaren" | "djelomicno" | "neostvaren" | "nije_se_radilo";
export interface TaskOutcome {
  id: string;
  takt_id: string;
  datum: string;
  status: OutcomeStatus;
  komentar: string | null;
  fotografije: string[];
}
export interface TaskOutcomeSummary {
  id: string;
  datum: string;
  status: OutcomeStatus;
  komentar: string | null;
  broj_fotografija: number;
  dan_id: string | null;
  dan_status: SiteDay["status"] | null;
  zakljucen: boolean;
}
export interface SiteDay {
  id: string;
  gradiliste_id: string;
  datum: string;
  status: "otvoren" | "zakljucen" | "potvrdjen_voditelj" | "odobren_ured";
  zakljucio_id: string;
  zakljuceno_at: string;
  potvrdio_id: string | null;
  potvrdjeno_at: string | null;
  rok_potvrde_at: string;
  kasni_potvrda: boolean;
  ciklus: number;
}
export interface PeriodLock {
  id: string;
  gradiliste_id: string;
  godina: number;
  mjesec: number;
  zakljucao_id: string;
  zakljucano_at: string;
  otkljucao_id: string | null;
  otkljucano_at: string | null;
  razlog_otkljucavanja: string | null;
}
export interface ChangeRequest {
  id: string;
  dan_id: string;
  raspored_id: string | null;
  tekst: string;
  status: "poslan" | "odobren" | "odbijen";
  podnio_id: string;
  odgovor: string | null;
  odlucio_id: string | null;
  odluceno_at: string | null;
  created_at: string;
  ciklus: number;
}
export interface RequestMessage {
  id: string;
  zahtjev_id: string;
  autor_id: string;
  tekst: string;
  created_at: string;
}
export interface OfficeHours extends Omit<ZapisSati, "status"> {
  status: ZapisSati["status"] | "potvrdjen_voditelj" | "korigiran";
  razlog_korekcije?: string | null;
}
export interface CorrectionInput {
  confirm_sick_leave?: boolean;
  tip_zapisa: "rad" | "nenazocnost";
  vrsta_nenazocnosti: string | null;
  pocetak: string | null;
  kraj: string | null;
  zastoji_min: number;
  teren: boolean;
  razlog_korekcije: string;
}

export function hasPermission(
  user: CurrentUser | null,
  module: string,
  action: string,
  scope?: string,
): boolean {
  return !!user?.permissions?.some(
    (p) =>
      p.module === module &&
      p.action === action &&
      (!scope || p.scope === scope),
  );
}
export function isFieldUser(user: CurrentUser | null): boolean {
  return !!user?.roles?.some((r) =>
    ["poslovodja", "voditelj", "zamjenik"].includes(r.code),
  );
}
export function officeAccess(user: CurrentUser | null): boolean {
  return (
    !isFieldUser(user) &&
    (hasPermission(user, "sati", "korekcija", "all") ||
      hasPermission(user, "sati", "odobri", "all"))
  );
}
export function managerWindow(user: CurrentUser | null): boolean {
  return (
    !!user?.roles?.some((r) => ["voditelj", "zamjenik"].includes(r.code)) ||
    hasPermission(user, "sati", "potvrdi", "team")
  );
}

export const outcomeLabels: Record<OutcomeStatus, string> = {
  ostvaren: "Ostvaren",
  djelomicno: "Djelomično",
  neostvaren: "Neostvaren",
  nije_se_radilo: "Nije se radilo",
};
export const needsComment = (status: OutcomeStatus) => status !== "ostvaren";
export const needsPhoto = (status: OutcomeStatus) =>
  status === "ostvaren" || status === "djelomicno";
export function missingEvidence(outcome: TaskOutcome | null): string[] {
  if (!outcome) return ["dnevni status"];
  return [
    needsComment(outcome.status) && !outcome.komentar?.trim()
      ? "komentar"
      : null,
    needsPhoto(outcome.status) && !outcome.fotografije.length
      ? "fotografija"
      : null,
  ].filter((x): x is string => !!x);
}
export function activeOn(task: SiteTask, day: string): boolean {
  return (
    task.status === "aktivan" &&
    task.planirani_od <= day &&
    day <= task.planirani_do
  );
}
export function canReopenOwn(
  day: SiteDay,
  userId: string,
  now = Date.now(),
): boolean {
  return (
    day.status === "zakljucen" &&
    day.zakljucio_id === userId &&
    now < Date.parse(day.rok_potvrde_at)
  );
}
export function intervalsOverlap(a: CrewMember, b: CrewMember): boolean {
  return (
    a.vrijedi_od <= (b.vrijedi_do ?? "9999-12-31") &&
    b.vrijedi_od <= (a.vrijedi_do ?? "9999-12-31")
  );
}
export interface DateRange {
  start: string;
  end: string;
}
function adjacentDay(date: string, offset: number): string {
  const value = new Date(`${date}T12:00:00Z`);
  value.setUTCDate(value.getUTCDate() + offset);
  return value.toISOString().slice(0, 10);
}
/** Compare each interval boundary, preserving closed days whose membership stays unchanged. */
export function changedMembershipRanges(
  before: CrewMember[],
  after: CrewMember[],
): DateRange[] {
  const ranges: DateRange[] = [];
  for (const member of after) {
    if (!member.vrijedi_od) continue;
    const old = member.id
      ? before.find((row) => row.id === member.id)
      : undefined;
    const end = member.vrijedi_do ?? "9999-12-31";
    if (!old) {
      ranges.push({ start: member.vrijedi_od, end });
      continue;
    }
    const oldEnd = old.vrijedi_do ?? "9999-12-31";
    const boundaries = [
      ...new Set([
        old.vrijedi_od,
        member.vrijedi_od,
        ...(oldEnd < "9999-12-31" ? [adjacentDay(oldEnd, 1)] : []),
        ...(end < "9999-12-31" ? [adjacentDay(end, 1)] : []),
      ]),
    ].sort();
    boundaries.forEach((start, index) => {
      const was = old.vrijedi_od <= start && start <= oldEnd;
      const becomes = member.vrijedi_od <= start && start <= end;
      if (was !== becomes || (was && old.je_brigadir !== member.je_brigadir))
        ranges.push({
          start,
          end: boundaries[index + 1]
            ? adjacentDay(boundaries[index + 1]!, -1)
            : "9999-12-31",
        });
    });
  }
  return ranges;
}
/** Inclusive intervals mirror the database constraints; no history is removed. */
export function validateMembers(members: CrewMember[]): string | null {
  if (!members.length) return "Dodajte barem jednog člana i brigadira.";
  for (const [index, member] of members.entries()) {
    if (!member.radnik_id || !member.vrijedi_od)
      return "Odaberite radnika i prvi dan rada u ekipi.";
    if (member.vrijedi_do && member.vrijedi_do < member.vrijedi_od)
      return "Zadnji dan rada u ekipi ne smije biti prije prvoga.";
    for (const other of members.slice(index + 1)) {
      if (
        intervalsOverlap(member, other) &&
        member.radnik_id === other.radnik_id
      )
        return "Dani rada istog radnika u ekipi ne smiju se preklapati.";
      if (
        intervalsOverlap(member, other) &&
        member.je_brigadir &&
        other.je_brigadir
      )
        return "Brigada ne može imati dva brigadira u istom razdoblju.";
    }
  }
  if (!members.some((m) => m.je_brigadir))
    return "Brigada mora imati brigadira iz svojih članova.";
  return null;
}

export const workflow = {
  crews: async (site: string, signal?: AbortSignal) =>
    (
      await api.get<Crew[]>("/brigade", {
        params: { gradiliste_id: site || undefined },
        signal,
      })
    ).data,
  createCrew: async (site: string, name: string, members: CrewMember[]) =>
    (
      await api.post<Crew>("/brigade", {
        gradiliste_id: site,
        naziv: name,
        clanovi: members.map(
          ({ radnik_id, je_brigadir, vrijedi_od, vrijedi_do }) => ({
            radnik_id,
            je_brigadir,
            vrijedi_od,
            vrijedi_do,
          }),
        ),
      })
    ).data,
  updateCrew: async (id: string, name: string, active: boolean) =>
    (await api.patch<Crew>(`/brigade/${id}`, { naziv: name, aktivna: active }))
      .data,
  updateMembers: async (id: string, members: CrewMember[]) =>
    (
      await api.put<Crew>(`/brigade/${id}/clanovi`, {
        clanovi: members.map(
          ({ id, radnik_id, je_brigadir, vrijedi_od, vrijedi_do }) => ({
            ...(id ? { id } : {}),
            radnik_id,
            je_brigadir,
            vrijedi_od,
            vrijedi_do,
          }),
        ),
      })
    ).data,
  tasks: async (site: string, signal?: AbortSignal) =>
    (
      await api.get<SiteTask[]>("/taktovi", {
        params: { gradiliste_id: site },
        signal,
      })
    ).data,
  createTask: async (site: string, data: TaskInput) =>
    (await api.post<SiteTask>("/taktovi", { ...data, gradiliste_id: site }))
      .data,
  updateTask: async (id: string, data: TaskInput) =>
    (await api.patch<SiteTask>(`/taktovi/${id}`, data)).data,
  outcomes: async (id: string, signal?: AbortSignal) =>
    (await api.get<TaskOutcomeSummary[]>(`/taktovi/${id}/dani`, { signal }))
      .data,
  outcome: async (
    id: string,
    day: string,
    signal?: AbortSignal,
  ): Promise<TaskOutcome | null> => {
    try {
      return (
        await api.get<TaskOutcome>(`/taktovi/${id}/dani/${day}`, { signal })
      ).data;
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 404 &&
        error.response.data?.detail === "Dnevni status nije pronađen."
      )
        return null;
      throw error;
    }
  },
  saveOutcome: async (
    id: string,
    day: string,
    status: OutcomeStatus,
    comment: string,
  ) =>
    (
      await api.put<TaskOutcome>(`/taktovi/${id}/dani/${day}`, {
        status,
        komentar: comment.trim() || null,
      })
    ).data,
  uploadPhoto: async (id: string, day: string, file: File) => {
    const form = new FormData();
    form.append("datoteka", file);
    return (
      await api.post<{ dokument_id: string }>(
        `/taktovi/${id}/dani/${day}/fotografije`,
        form,
      )
    ).data;
  },
  photo: async (
    id: string,
    day: string,
    document: string,
    signal?: AbortSignal,
  ) =>
    (
      await api.get<Blob>(
        `/taktovi/${id}/dani/${day}/fotografije/${document}`,
        { responseType: "blob", signal },
      )
    ).data,
  days: async (
    params: { gradiliste_id?: string; datum?: string; kasni_potvrda?: boolean },
    signal?: AbortSignal,
  ) =>
    (
      await api.get<SiteDay[]>("/dani-rasporeda", {
        params: { ...params, limit: 500 },
        signal,
      })
    ).data,
  dayAction: async (
    site: string,
    day: string,
    action: "zakljuci" | "potvrdi" | "otvori" | "preuzmi-potvrdu",
    data?: { razlog?: string; zahtjev_id?: string },
  ) =>
    (
      await api.post<SiteDay>(
        `/dani-rasporeda/${site}/${day}/${action}`,
        data ?? {},
      )
    ).data,
  hours: async (site: string, day: string, signal?: AbortSignal) =>
    (
      await api.get<OfficeHours[]>("/sati", {
        params: { gradiliste_id: site, datum: day, limit: 500 },
        signal,
      })
    ).data,
  correct: async (id: string, data: CorrectionInput) =>
    (await api.patch<OfficeHours>(`/sati/${id}/korekcija`, data)).data,
  approve: async (id: string) =>
    (await api.post<OfficeHours>(`/sati/${id}/odobri`)).data,
  periods: async (site: string, signal?: AbortSignal) =>
    (
      await api.get<PeriodLock[]>("/razdoblja", {
        params: { gradiliste_id: site, limit: 500 },
        signal,
      })
    ).data,
  periodAction: async (
    site: string,
    month: string,
    unlock: boolean,
    reason = "",
  ) => {
    const [year, number] = month.split("-");
    return (
      await api.post<PeriodLock>(
        `/razdoblja/${site}/${Number(year)}/${Number(number)}/${unlock ? "otkljucaj" : "zakljucaj"}`,
        unlock ? { razlog: reason.trim() } : {},
      )
    ).data;
  },
  requests: async (day?: string, signal?: AbortSignal) =>
    (
      await api.get<ChangeRequest[]>("/zahtjevi-izmjene", {
        params: { dan_id: day, limit: 500 },
        signal,
      })
    ).data,
  createRequest: async (day: string, text: string) =>
    (
      await api.post<ChangeRequest>("/zahtjevi-izmjene", {
        dan_id: day,
        tekst: text.trim(),
      })
    ).data,
  messages: async (id: string, signal?: AbortSignal) =>
    (
      await api.get<RequestMessage[]>(`/zahtjevi-izmjene/${id}/poruke`, {
        signal,
      })
    ).data,
  message: async (id: string, text: string) =>
    (
      await api.post<RequestMessage>(`/zahtjevi-izmjene/${id}/poruke`, {
        tekst: text.trim(),
      })
    ).data,
  decide: async (id: string, status: "odobren" | "odbijen", answer: string) =>
    (
      await api.post<ChangeRequest>(`/zahtjevi-izmjene/${id}/odluka`, {
        status,
        odgovor: answer.trim(),
      })
    ).data,
};
